import { useState } from 'react';
import './manage-game-sidebar.styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faBook, faBriefcase, faCaretRight, faComments, faFlagCheckered, faGear, faGem, faGlobeAmericas, faHandshake, faHouse, faListCheck, faMagicWandSparkles, faPeopleGroup, faPeopleLine, faPiggyBank, faPuzzlePiece, faShoppingBag, faShoppingBasket, faShoppingCart, faSliders, faStore, faStoreAlt, faTicket, faUserSlash, faUsers } from '@fortawesome/free-solid-svg-icons';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import InfoLinks from '@/components/SHARED/info-links/info-links.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { membershipRestrictions } from '@/utils/membershipRestrictions';
import { setShowUpgradeModal } from '@/state/slices/contentSlice';
import { useEffectiveMembership } from '@/utils/useEffectiveMembership';

const ManageGameSidebar = ({
  gameData,
  actionItems,
  gameElements,
  playerList,
  setShowFeedbackModal,
  setShowGameInfoModal,
  role
}) => {

  const membership = useEffectiveMembership();
  const isManager = role === 'owner' || role === 'manager';
  const [pendingActionItems, setPendingActionItems] = useState(0);
  const [pendingRealWorldItems, setPendingRealWorldItems] = useState(0);
  const [showSettingsDrop, setShowSettingsDrop] = useState(false);
  const [showMarketplaceDrop, setShowMarketplaceDrop] = useState(false);
  const [disabled, setDisabled] = useState([]);
  const [basePath, setBasePath] = useState('/manage');
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!gameData) { return; }
    if (location.pathname.includes('/preview/manager')) {
      setBasePath('/preview/manager');
    }
  }, [gameData])

  useEffect(() => {
    if (!membershipRestrictions) return;
    const baseRestrictions = membershipRestrictions[0];
    setDisabled(baseRestrictions.blocked);
    if (!membership || !gameElements) return;
    const restrictions = membershipRestrictions[membership?.membership];
    if (!restrictions) return;
    let blockedElements = [...restrictions.blocked];
    if (
      Object.values(gameElements)
        .some(e =>
          e.type === 'item' &&
          e.opt_itemType === 'realworld' &&
          e.status === 'active'
        ) === false
    ) {
      blockedElements.push('realworld');
    }

    if (
      Object.values(gameElements)
        .some(e =>
          (e.type === 'item' && e.opt_itemType === 'inventory') ||
          e.type === 'challenge'
        ) === false
    ) {
      blockedElements.push('actionItems');
    }
    setDisabled(blockedElements);
  }, [membershipRestrictions, membership, gameElements])

  useEffect(() => {
    if (!actionItems || !playerList || !gameElements) return;
    const pendingAI = Object.values(actionItems)
      .filter(i =>
        i.status === 'pending' &&
        i.type !== 'realworld' &&
        playerList[i.playerId] &&
        (
          (
            i.type === 'checkpoint' &&
            gameElements[i.questId]
          ) ||
          gameElements[i.elementId]
        )
      ).length;
    setPendingActionItems(pendingAI);
    const pendingRW = Object.values(actionItems)
      .filter(i =>
        i.status === 'pending' &&
        i.type === 'realworld' &&
        playerList[i.playerId] &&
        gameElements[i.elementId]
      ).length;
    setPendingRealWorldItems(pendingRW);
  }, [actionItems, playerList, gameElements])

  useEffect(() => {
    if (
      location.pathname.includes('/marketplace') ||
      location.pathname.includes('/contributions') ||
      location.pathname.includes('/lottery')
    ) {
      setShowMarketplaceDrop(true);
    } else {
      setShowMarketplaceDrop(false);
    }
    if (location.pathname.includes('/settings')) {
      setShowSettingsDrop(true);
    } else {
      setShowSettingsDrop(false);
    }
  }, [location])

  useEffect(() => {
    if (showSettingsDrop) {
      const el = document.getElementById('menu-container');
      if (!el) return;
      el.style.scrollBehavior = "smooth"; // Enable smooth scrolling
      let timeout = setTimeout(() => {
        el.scrollTop += 300;
        clearTimeout(timeout);
      }, 250);
    }
  }, [showSettingsDrop])



  return (
    <div className='manage-game-sidebar'>
      <div className='sidebar-container'>
        <div className='game-header'>
          <div className='image-div'>
            <ImageContainer
              src={gameData.header}
              className={`header-image ${gameData.border_header}-border`} />
          </div>
          <div className='title' style={{ 'color': gameData.color }}>{gameData.gameName}</div>
          <button
            className='g-button tiny-btn'
            onClick={() => setShowGameInfoModal(true)}>
            View Game Info
          </button>
        </div>
        {
          (isManager || role === 'distributor') &&
          <div className='manage-menu-container' id='menu-container'>
            <div className='manage-game-menu'>

              {/* Home button */}
              <Link to={`${basePath}/${gameData.path}`}>
                <button
                  className={`g-button text-only menu-item ${
                    location.pathname === `${basePath}/${gameData.path}` ||
                    location.pathname === `${basePath}/${gameData.path}/` ?
                    'active' :
                    ''
                    }`} >
                  <FontAwesomeIcon
                    icon={faHouse}
                    className='menu-item-icon'
                    size='xl'
                  />
                  <span>Home</span>
                </button>
              </Link>

              {/* Players button */}
              <Link to={`${basePath}/${gameData.path}/players`}>
                <button
                  className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/players`) ?
                    'active' :
                    ''
                    }`}>
                  <FontAwesomeIcon
                    icon={faUsers}
                    className='menu-item-icon'
                    size='xl'
                  />
                  <span>Players</span>
                </button>
              </Link>

              {/* Teams button */}
              {
                (gameData && gameData.access.includes('teams')) &&
                <Link to={`${basePath}/${gameData.path}/teams`}>
                  <button
                    className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/teams`) ?
                      'active' :
                      ''
                      }`} >
                    <FontAwesomeIcon
                      icon={faPeopleGroup}
                      className='menu-item-icon'
                      size='xl'
                    />
                    <span>Teams</span>
                  </button>
                </Link>
              }

              {/* Rewards button */}
              <Link to={`${basePath}/${gameData.path}/distribute`}>
                <button
                  className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/distribute`) ?
                    'active' :
                    ''
                    }`} >
                  <FontAwesomeIcon
                    icon={faGem}
                    className='menu-item-icon'
                    size='xl'
                  />
                  <span>Distribute Rewards</span>
                </button>
              </Link>


              {/* Action items button */}


              <Link to={`${basePath}/${gameData.path}/actionitems`}>
                <button
                  className={`g-button text-only menu-item manage-menu-action-items 
                                            ${pendingActionItems > 0 ?
                      'pending-items' :
                      ''}
                                            ${location.pathname.includes(`${basePath}/${gameData.path}/actionitems`) ?
                      'active' :
                      ''
                    }`} >
                  <div className="notification-with-badge">
                    <FontAwesomeIcon
                      icon={faBell}
                      className='menu-item-icon'
                      size='xl'
                    />
                    {
                      (pendingActionItems > 0) &&
                      <span className="badge">{pendingActionItems}</span>
                    }
                  </div>
                  <span>Action Items</span>
                </button>
              </Link>


              {/* Real World items button */}

              <Link to={`${basePath}/${gameData.path}/realworlddist`}>
                <button
                  className={`g-button text-only menu-item manage-menu-action-items 
                                            ${pendingRealWorldItems > 0 ?
                      'pending-items' :
                      ''}
                                            ${location.pathname.includes(`${basePath}/${gameData.path}/realworlddist`) ?
                      'active' :
                      ''
                    }`}>
                  <div className="notification-with-badge">
                    <FontAwesomeIcon
                      icon={faShoppingBag}
                      className='menu-item-icon'
                      size='xl'
                    />
                    {
                      (pendingRealWorldItems > 0) &&
                      <span className="badge">{pendingRealWorldItems}</span>
                    }
                  </div>
                  <span>Real World Dist.</span>
                </button>
              </Link>

              {/* Manage elements button */}

              <Link to={`${basePath}/${gameData.path}/elements`}>
                <button
                  className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/elements`) ?
                    'active' :
                    ''
                    }`} >
                  <FontAwesomeIcon
                    icon={faBriefcase}
                    className='menu-item-icon'
                    size='xl'
                  />
                  <span>Manage Elements</span>
                </button>
              </Link>


              {/* Marketplace button */}
              {
                (
                  isManager &&
                  gameData &&
                  gameData.access.includes('store')
                ) &&
                <>
                  <button
                    className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/marketplace`) ?
                      '' :
                      ''
                      }`}
                    onClick={() => setShowMarketplaceDrop(!showMarketplaceDrop)}
                  >
                    <FontAwesomeIcon
                      icon={faStore}
                      className='menu-item-icon'
                      size='xl'
                    />
                    <span>Marketplace</span>
                    <FontAwesomeIcon
                      icon={faCaretRight}
                      id='marketplace-drop-menu-caret'
                      className={
                        `menu-item-icon ${showMarketplaceDrop ?
                          'rotated' :
                          ''
                        }`
                      }
                    />
                  </button>
                  <div
                    className={`drop-container marketplace ${showMarketplaceDrop ? 'active' : ''
                      } ${membershipRestrictions &&
                        membership &&
                        !membershipRestrictions[membership.membership].blocked.includes('pointOfSale') ?
                        'plus1' :
                        ''
                      }`}
                    id='marketplace-drop-menu'
                  >
                    <div className='drop-menu'>
                      <Link to={`${basePath}/${gameData.path}/marketplace`}>
                        <button
                          className={
                            `g-button text-only drop-menu-item menu-item ${(
                              location.pathname.includes(`${basePath}/${gameData.path}/marketplace`) &&
                              !location.pathname.includes('/marketplace/')
                            ) ?
                              'active' :
                              ''
                            }`
                          } >
                          <FontAwesomeIcon
                            icon={faShoppingCart}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Game Store
                        </button>
                      </Link>
                      <Link to={`${basePath}/${gameData.path}/marketplace/purchases`}>
                        <button
                          className={
                            `g-button text-only drop-menu-item menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/marketplace/purchases`) ?
                              'active' :
                              ''}`
                          } >
                          <FontAwesomeIcon
                            icon={faShoppingBasket}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Purchases
                        </button>
                      </Link>
                      <Link to={disabled.includes('item_contribution') ? '#' : `${basePath}/${gameData.path}/contributions`}>
                        <button
                          className={
                            `g-button text-only drop-menu-item menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/contributions`) ?
                              'active' :
                              ''} ${disabled.includes('item_contribution') ?
                                'disabled' :
                                ''
                            }`
                          } >
                          <FontAwesomeIcon
                            icon={faPiggyBank}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Contributions
                        </button>
                      </Link>
                      <Link to={disabled.includes('item_lottery') ? '#' : `${basePath}/${gameData.path}/lottery`}>
                        <button
                          className={
                            `g-button text-only drop-menu-item menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/lottery`) ?
                              'active' :
                              ''} ${disabled.includes('item_lottery') ?
                                'disabled' :
                                ''
                            }`
                          } >
                          <FontAwesomeIcon
                            icon={faTicket}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Lotteries
                        </button>
                      </Link>
                      {/* <Link to={disabled.includes('tradePortal') ? '#' : `${basePath}/${gameData.path}/marketplace/tradeportal`}>
                        <button className={`g-button text-only drop-menu-item menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/marketplace/tradeportal`) ?
                          'active' :
                          ''} ${disabled.includes('tradePortal') ?
                          'disabled' :
                          ''
                        }`
                        }>
                          <FontAwesomeIcon
                            icon={faExchange}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Trade Portal
                        </button>
                      </Link> */}


                      <Link to={disabled.includes('pointOfSale') ? '#' : `${basePath}/${gameData.path}/marketplace/pos`}>
                        <button
                          className={
                            `g-button text-only drop-menu-item menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/marketplace/pos`) ?
                              'active' :
                              ''} ${disabled.includes('pointOfSale') ?
                                'disabled' :
                                ''
                            }`
                          } >
                          <FontAwesomeIcon
                            icon={faStoreAlt}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Point of Sale
                        </button>
                      </Link>
                    </div>
                  </div>
                </>
              }

              {/* Leaderboards button */}

              {
                (gameData.access.includes('leaderboards')) &&
                <Link to={`${basePath}/${gameData.path}/leaderboards`}>
                  <button
                    className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/leaderboards`) ?
                      'active' :
                      ''
                      }`} >
                    <FontAwesomeIcon
                      icon={faFlagCheckered}
                      className='menu-item-icon'
                      size='xl'
                    />
                    <span>Leaderboards</span>
                  </button>
                </Link>
              }

              {/* Explore data button */}
              <Link to={`${basePath}/${gameData.path}/activity`}>
                <div
                  className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/activity`) ?
                    'active' :
                    ''
                    }`} >
                  <FontAwesomeIcon
                    icon={faGlobeAmericas}
                    className='menu-item-icon'
                    size='xl'
                  />
                  <span>Game Activity</span>
                </div>
              </Link>

              {/* Settings button */}
              {
                isManager &&
                <>
                  <div
                    className={`g-button text-only menu-item ${location.pathname.includes(`${basePath}/${gameData.path}/settings`) ?
                      '' :
                      ''
                      }`}
                    onClick={() => setShowSettingsDrop(!showSettingsDrop)}
                  >
                    <FontAwesomeIcon
                      icon={faGear}
                      className='menu-item-icon'
                      size='xl'
                    />
                    <span>Game Settings</span>
                    <FontAwesomeIcon
                      icon={faCaretRight}
                      id='settings-drop-menu-caret'
                      className={
                        `menu-item-icon ${showSettingsDrop ?
                          'rotated' :
                          ''
                        }`
                      }
                    />
                  </div>
                  <div
                    className={`drop-container settings ${showSettingsDrop ? 'active' : ''}`}
                    id='settings-drop-menu'
                  >
                    <div className='drop-menu'>
                      <Link to={`${basePath}/${gameData.path}/settings`}>
                        <div
                          className={
                            `drop-menu-item menu-item ${location.pathname === (`${basePath}/${gameData.path}/settings`) ?
                              'active' :
                              ''}`
                          } >
                          <FontAwesomeIcon
                            icon={faListCheck}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Information
                        </div>
                      </Link>
                      <Link to={`${basePath}/${gameData.path}/settings/design`}>
                        <div
                          className={
                            `drop-menu-item menu-item ${location.pathname.includes(`settings/design`) ?
                              'active' :
                              ''}`
                          } >
                          <FontAwesomeIcon
                            icon={faMagicWandSparkles}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Game Design
                        </div>
                      </Link>
                      <Link to={`${basePath}/${gameData.path}/settings/rostering`}>
                        <div
                          className={
                            `drop-menu-item menu-item ${location.pathname.includes('settings/rostering') ?
                              'active' :
                              ''}`
                          } >
                          <FontAwesomeIcon
                            icon={faPeopleLine}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Rostering
                        </div>
                      </Link>
                      <Link to={`${basePath}/${gameData.path}/settings/elements`}>
                        <div
                          className={
                            `drop-menu-item menu-item ${location.pathname.includes('settings/elements') ?
                              'active' :
                              ''}`
                          } >
                          <FontAwesomeIcon
                            icon={faSliders}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Element Options
                        </div>
                      </Link>
                      <Link to={`${basePath}/${gameData.path}/settings/removedPlayers`}>
                        <div
                          className={
                            `drop-menu-item menu-item ${location.pathname.includes('settings/removedplayers') ?
                              'active' :
                              ''}`
                          } >
                          <FontAwesomeIcon
                            icon={faUserSlash}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Removed Players
                        </div>
                      </Link>
                      <Link to={
                        disabled.includes('collaborators')
                          ? "#"
                          : `${basePath}/${gameData.path}/settings/collaborators`}
                        onClick={disabled.includes('collaborators')
                          ? ((e) => {
                            e.preventDefault(); dispatch(setShowUpgradeModal({
                              message: `Sorry! Because you are using the ${membership?.membership === 0 ? 'Free' : 'Pro'} Plan, you cannot add Collaborators to this game.`,
                              closeButton: true
                            }))
                          }) : (() => null)}>
                        <div
                          className={
                            `drop-menu-item menu-item ${location.pathname === (`${basePath}/${gameData.path}/settings/collaborators`) ?
                              'active' :
                              ''
                            } ${disabled.includes('collaborators') ?
                              'disabled' :
                              ''
                            }`
                          } >
                          <FontAwesomeIcon
                            icon={faPuzzlePiece}
                            className='drop-menu-item-icon'
                            size='lg'
                          />
                          Collaborators
                        </div>
                      </Link>

                    </div>
                  </div>

                  <Link to='/resources' target="_blank">
                    <button
                      className='g-button text-only menu-item'>
                      <FontAwesomeIcon
                        icon={faBook}
                        className='menu-item-icon'
                        size='xl'
                      />
                      <span>Resource Center</span>
                    </button>
                  </Link>

                  {/* Feedback button */}
                  <button
                    className='g-button text-only menu-item manage-menu-bug'
                    onClick={() => {
                      setShowFeedbackModal(true);
                    }}>
                    <FontAwesomeIcon
                      icon={faComments}
                      className='menu-item-icon'
                      size='xl'
                    />
                    <span>Send Us Feedback</span>
                  </button>
                </>
              }
            </div>
          </div>
        }
        {/* <div className="g-space-40"></div> */}
        <div className='manage-menu-footer'>
          <InfoLinks />
        </div>
      </div>
    </div>
  )
}

export default ManageGameSidebar